/**
 * Availability of a ClickHouse-MEASURED quantity, as opposed to a static
 * catalogue fact. Three states, because "no number" has two very different
 * causes and the UI must not conflate them:
 *
 *   loading      — the query is in flight; render a skeleton.
 *   unavailable  — the query settled with nothing (ClickHouse down, or no
 *                  Morningstar data). Render an em dash. NEVER render 0: a
 *                  confident zero is a lie the user cannot detect.
 *   ready        — a real measurement. A 0 here is a genuine 0.
 *
 * Derive this from the METRIC query itself, never from the catalogue: since the
 * catalogue endpoint always returns the six static universes regardless of CH
 * health, its length says nothing about whether ClickHouse is reachable.
 */
export type DataStatus = 'loading' | 'unavailable' | 'ready';

/**
 * `isLoading` wins; an empty settled result is 'unavailable', not 'ready'.
 *
 * The app-wide query client sets `retry: false`, so an errored query reports
 * `isLoading === false` — errors therefore land in 'unavailable' rather than
 * being stuck in a permanent skeleton.
 */
export function toDataStatus(isLoading: boolean, hasData: boolean): DataStatus {
  if (isLoading) return 'loading';
  return hasData ? 'ready' : 'unavailable';
}

/** The em dash every "unavailable" number renders. */
export const UNAVAILABLE_DASH = '—';

export const UNAVAILABLE_TITLE =
  'Live index metrics are unavailable right now: this figure is not a zero.';
